import { action, computed, makeObservable, observable } from 'mobx';
import { YesNoState } from '@alfa-retail-react/components/yes-no-question';

import {
    MAX_LENGTH_CARDHOLDER_NAME,
    MAX_LENGTH_LATIN,
    MIN_LENGTH_LATIN,
} from 'app/constants/baseConstants';
import {
    getConcatedCardHolderName,
    getWithShortName,
    getWithShortSurname,
} from 'app/utils/StringUtils';
import { AddressFormStore } from 'app/logic/address/AddressFormStore';
import { Reference } from 'app/model/Reference';
import { BirthPlaceFormStore } from 'app/logic/pages/order/BirthPlaceFormStore';
import { CardOrder } from 'app/model/CardOrder';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';
import { getFIOAllowedCharactersForFirstAndLastSymbolValidation } from 'app/form/ValidationRules';
import { MarketingConsent } from 'app/model/MarketingConsent';

/**
 * Проверка персонифицированных данных. Если поля в них отсутствуют - данных либо нет, либо они были введены из черновика
 * и могут быть изменены пользователем.
 *
 * @param personalized
 */
const getIsLatinEditable = (
    nameLatin: string,
    surnameLatin: string,
): boolean => {
    const isNameExists = !!nameLatin;
    const isSurnameExists = !!surnameLatin;

    if (!isNameExists || !isSurnameExists) {
        return true;
    }

    const [symbolValidation] =
        getFIOAllowedCharactersForFirstAndLastSymbolValidation();

    const isSymbolValidationFailed =
        !symbolValidation.test(nameLatin.toUpperCase()) ||
        !symbolValidation.test(surnameLatin.toUpperCase());
    const isLengthValidationFailed =
        nameLatin.length > MAX_LENGTH_LATIN ||
        surnameLatin.length > MAX_LENGTH_LATIN ||
        nameLatin.length < MIN_LENGTH_LATIN ||
        surnameLatin.length < MIN_LENGTH_LATIN;

    return isSymbolValidationFailed || isLengthValidationFailed;
};

/**
 * @param latinCredentials используется для отображения ФИ на латинице из черновика, если они присутствуют
 */
interface TOrderFormStore {
    cardOrder: CardOrder;
    personalizedOrder: CardOrder;
    localityTypesReference: Array<Reference>;
    streetTypesReference: Array<Reference>;
    country: Reference;
    localityTypesStore: LocalityTypeStore;
    latinCredentials: {
        surnameLatin: string;
        nameLatin: string;
    };
}

export class OrderFormStore {
    @observable
    surnameLatin: string;

    @observable
    nameLatin: string;

    @observable
    cardholderName: string;

    isLatinEditable: boolean;

    @observable
    secretWord: string;

    @observable
    email: string;

    birthPlaceFormStore: BirthPlaceFormStore;

    registrationAddress: AddressFormStore;

    @observable
    sameAddress: YesNoState;

    @observable
    residentialAddress?: AddressFormStore;

    @observable
    fatca: YesNoState;

    @observable
    isMarketingConsentSigned: boolean;

    @observable
    marketingConsentList?: MarketingConsent[];

    constructor({
        cardOrder,
        personalizedOrder,
        localityTypesReference,
        streetTypesReference,
        country,
        latinCredentials,
        localityTypesStore,
    }: TOrderFormStore) {
        makeObservable(this);

        this.surnameLatin = latinCredentials.surnameLatin;
        this.nameLatin = latinCredentials.nameLatin;
        this.isLatinEditable = getIsLatinEditable(
            personalizedOrder.client.nameLatin,
            personalizedOrder.client.surnameLatin,
        );
        this.secretWord = cardOrder.client.secretWord;
        this.email = cardOrder.client.email;
        this.cardholderName = OrderFormStore.getProcessedCardholderName(
            cardOrder,
            latinCredentials,
        );
        this.fatca = cardOrder.client.isUnderFatca
            ? YesNoState.YES
            : YesNoState.NO;
        this.isMarketingConsentSigned =
            !!cardOrder.client.isMarketingConsentSigned;
        this.marketingConsentList = cardOrder.client.marketingConsentList;

        if (
            !cardOrder.client.residentialAddress &&
            !cardOrder.client.registrationAddress
        ) {
            this.sameAddress = YesNoState.YES;
        } else {
            this.sameAddress = cardOrder.client.registrationAddress.equals(
                cardOrder.client.residentialAddress,
            )
                ? YesNoState.YES
                : YesNoState.NO;
        }

        this.birthPlaceFormStore = new BirthPlaceFormStore(
            cardOrder.client.birthPlace,
            localityTypesReference,
            localityTypesStore,
        );
        this.registrationAddress = new AddressFormStore(
            cardOrder.client.registrationAddress,
            streetTypesReference,
            country,
            localityTypesStore,
        );
        this.residentialAddress = new AddressFormStore(
            cardOrder.client.residentialAddress,
            streetTypesReference,
            country,
            localityTypesStore,
        );
    }

    initialize() {
        return Promise.all([
            this.birthPlaceFormStore.initialize(),
            this.registrationAddress.initialize(),
            this.residentialAddress.initialize(),
        ]);
    }

    @computed
    get notSameAddress() {
        return this.sameAddress === YesNoState.NO;
    }

    @computed
    get cardHolder() {
        return this.cardholderName;
    }

    @computed
    get surname() {
        return this.surnameLatin;
    }

    @computed
    get name() {
        return this.nameLatin;
    }

    @computed
    get isUnderFatca() {
        return this.fatca === YesNoState.YES;
    }

    /**
     * Возвращает сокращенный по-умолчанию вариант написания имени и фамилии заказчика на латинице
     * @param name
     * @param surname
     */
    static getCardholderName(name: string, surname: string) {
        let result = getConcatedCardHolderName(name, surname);

        if (result.length > MAX_LENGTH_CARDHOLDER_NAME) {
            return getWithShortName(name, surname);
        }

        return result;
    }

    /**
     * Возвращает значение, соответствующее выбранному или новому имени-фамилии владельца карточки.
     * Соответствует требованиям сокращения cardholderName.
     * @param cardOrder
     * @param latinCredentials
     */
    static getProcessedCardholderName(
        cardOrder: CardOrder,
        latinCredentials: {
            nameLatin: string;
            surnameLatin: string;
        },
    ): string {
        const { nameLatin, surnameLatin } = latinCredentials;

        const cardHolderName = OrderFormStore.getCardholderName(
            nameLatin,
            surnameLatin,
        );
        const isCardHolderProcessed =
            cardOrder.cardholderName === cardHolderName ||
            cardOrder.cardholderName ===
                getWithShortSurname(nameLatin, surnameLatin);

        return isCardHolderProcessed
            ? cardOrder.cardholderName
            : cardHolderName;
    }

    @action
    setCardHolder(value: string) {
        this.cardholderName = value;
    }

    @action
    setSameAddress(sameAddress: YesNoState) {
        this.sameAddress = sameAddress;
    }

    @action
    setResidentialAddress(address: AddressFormStore) {
        this.residentialAddress = address;
    }

    @action
    setFatca(fatca: YesNoState) {
        this.fatca = fatca;
    }

    @action
    setSurnameLatin(surnameLatin: string) {
        this.surnameLatin = surnameLatin;

        this.cardholderName = OrderFormStore.getCardholderName(
            this.nameLatin,
            surnameLatin,
        );
    }

    @action
    setNameLatin(nameLatin: string) {
        this.nameLatin = nameLatin;

        this.cardholderName = OrderFormStore.getCardholderName(
            nameLatin,
            this.surnameLatin,
        );
    }

    @action
    setSecretWord = (secretWord: string) => {
        this.secretWord = secretWord;
    };

    @action
    setEmail = (email: string) => {
        this.email = email;
    };

    @action
    setIsMarketingConsentSigned = (isSigned: boolean) => {
        this.isMarketingConsentSigned = isSigned;
    };

    getMarketingConsentByType = (type: string): MarketingConsent | null =>
        this.marketingConsentList?.find(
            ({ consentType }) => consentType === type,
        );

    @action
    setMarketingConsentTypeSigned = (type: string, isSigned: boolean) => {
        const consentType = this.getMarketingConsentByType(type);

        if (consentType) {
            consentType.isSigned = isSigned;
        }
    };

    @action
    setAllMarketingConsentTypeSigned = (isSigned: boolean) => {
        this.marketingConsentList = this.marketingConsentList?.map((item) => ({
            ...item,
            isSigned,
        }));
    };

    getMarketingConsentTypeSigned = (type: string): boolean => {
        return this.getMarketingConsentByType(type)?.isSigned;
    };
}
